import React, { useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router';

import { ServiceRowCard, ServiceRowCardVariant } from '@/shared/ui';

import { ErzPermit } from '@/modules/erz/pages/services/ErzExtract';

import { ROUTES_BY_SERVICE_CODE } from '@/router/config';

import { saveFileFromBlob } from '@/helpers/saveFileFromBlob';

import { FeatureContext } from '@/contexts/Features/FeatureContext';
import ModalContext from '@/contexts/Modal/ModalContext';
import { ModalName } from '@/contexts/Modal/modalTypes';

import { SERVICE_CODES } from '@/lib/consts';

import { FeatureName } from '@/api/types';

import api from '@/api';

import css from './PermitList.module.scss';

interface Props {
  erzExtracts: ErzPermit[];
}

export const PermitList: React.FC<Props> = ({ erzExtracts }) => {
  const [extractIdLoading, setExtractIdLoading] = useState<string>('');
  const navigate = useNavigate();
  const { renderSpecificModal } = useContext(ModalContext);
  const { isFeatureAvailable } = useContext(FeatureContext);

  const checkIsExtractable = useCallback(
    (extract: ErzPermit): boolean =>
      extract.permitInfo.canExtract &&
      (!extract.permitInfo.expiredAt || new Date(extract.permitInfo.expiredAt) > new Date()),
    [],
  );

  const downloadExtract = (id: string): void => {
    if (!!extractIdLoading) return;

    setExtractIdLoading(id);

    api.permits
      .downloadExtract(id)
      .then(({ file, fileName }) => saveFileFromBlob(fileName ?? '', file))
      .catch(() => {
        renderSpecificModal({
          modalName: ModalName.ServerErrorAlert,
        });
        return;
      })
      .finally(() => setExtractIdLoading(''));
  };

  return (
    <div className={css.permitList}>
      {erzExtracts.map((extract) => (
        <ServiceRowCard
          key={extract.id}
          variant={ServiceRowCardVariant.PERMIT}
          data={extract}
          onDownloadExtract={checkIsExtractable(extract) ? (): void => downloadExtract(extract.id) : undefined}
          onDeletePermit={
            extract.permitInfo.permitType === SERVICE_CODES.TEMPORARY_RESIDENCE_RELOCATION &&
            isFeatureAvailable(FeatureName.ERZ_SERVICES) &&
            isFeatureAvailable(FeatureName.ERZ_CANCELLATION_TEMPORARY_RESIDENCE_RELOCATION)
              ? (): void => {
                  navigate(ROUTES_BY_SERVICE_CODE[SERVICE_CODES.TEMPORARY_RESIDENCE_RELOCATION]);
                }
              : undefined
          }
        />
      ))}
    </div>
  );
};
